import { request } from '@umijs/max';
import dayjs from 'dayjs';

export type TeacherBrief = {
  id: number;
  name: string;
  email: string;
  access: string;
};

export type TeacherAccount = {
  id: number;
  email: string;
  name: string;
  phone: string;
  role: string;
  active: boolean;
};

export type InviteCode = {
  id: number;
  created_by: number;
  created_at: string;
  expires_at: string;
  used_at?: string;
  used_by?: number;
};

export type CreatedInviteCode = InviteCode & {
  code: string;
};

export type Student = {
  id: number;
  name: string;
  school: string;
  grade: string;
  class_name: string;
  notes: string;
  teacher_id: number;
  owner_name?: string;
  access?: string;
  teachers?: TeacherBrief[];
};

export type Group = {
  id: number;
  name: string;
  students: Student[];
};

export type ReviewTask = {
  id: number;
  event_id: number;
  student_id: number;
  student_name: string;
  part_label: string;
  title: string;
  due_date: string;
  status: string;
  task_kind: string;
  subject: string;
  event_title: string;
  event_date: string;
};

export type Attachment = {
  id: number;
  event_id: number;
  filename: string;
  content_type: string;
  ocr_text: string;
  ocr_score: string;
  url: string;
  created_at: string;
};

export type AcademicEvent = {
  id: number;
  student_id: number;
  student_name: string;
  school: string;
  subject: string;
  category: string;
  title: string;
  event_date: string;
  lead_days: number;
  notes: string;
  score: string;
  status?: string;
  print_on: string;
  days_left: number;
  review_tasks: ReviewTask[];
  attachments: Attachment[];
};

export type HomeworkItem = {
  id: number;
  student_id: number;
  student_name: string;
  homework_date: string;
  subject: string;
  title: string;
  status: string;
  notes: string;
};

export type TodayData = {
  date: string;
  student_count: number;
  homework_total: number;
  homework_done: number;
  review_due: number;
  print_reminders: number;
  upcoming_events: number;
  print_items: AcademicEvent[];
  print_tasks: ReviewTask[];
  review_items: ReviewTask[];
  upcoming_items: AcademicEvent[];
};

export type PrintNoticeItem = {
  task_id: number;
  event_id: number;
  student_id: number;
  student_name: string;
  subject: string;
  title: string;
  category: string;
  category_label: string;
  event_date: string;
  print_on: string;
  lead_days: number;
  day_index: number;
  day_total: number;
  group_id: number | null;
  group_name: string;
  status: string;
  read: boolean;
};

export type PrintNoticeGroup = {
  group_id: number | null;
  group_name: string;
  items: PrintNoticeItem[];
};

export type PrintNoticeTab = {
  category: string;
  category_label: string;
  count: number;
  unread_count: number;
  groups: PrintNoticeGroup[];
};

export type PrintNotices = {
  date: string;
  is_today: boolean;
  total_count: number;
  unread_count: number;
  badge_count: number;
  tabs: PrintNoticeTab[];
};

export type AnswerPreview = {
  kind: string;
  url: string;
  html: string;
  hint: string;
  filename: string;
  page_count: number;
};

export type AnswerKey = {
  id: number;
  publisher: string;
  subject: string;
  grade: string;
  school: string;
  unit: string;
  page_from: number;
  page_to: number;
  title: string;
  filename: string;
  content_type: string;
  url: string;
  created_at: string;
  teachers?: TeacherBrief[];
};

export const SUBJECTS = ['中文', '英文', '數學'];
export const CATEGORY_OPTIONS = [
  { label: '默書', value: 'dictation' },
  { label: '小測', value: 'quiz' },
  { label: '考試', value: 'exam' },
];
export const HOMEWORK_STATUS = [
  { label: '待完成', value: 'todo' },
  { label: '已完成', value: 'done' },
  { label: '已核對', value: 'checked' },
  { label: '需重做', value: 'redo' },
];
export const PUBLISHERS = ['樂思', '現代', '培生', '牛津', '朗文', '香港教育圖書'];
export const GROUP_MIN_SIZE = 6;
export const GROUP_MAX_SIZE = 12;

export function daysUntil(date: string) {
  return dayjs(date).startOf('day').diff(dayjs().startOf('day'), 'day');
}

export function countdownText(date: string) {
  const days = daysUntil(date);
  if (days > 0) return `還有 ${days} 天`;
  if (days === 0) return '就是今天';
  return `已過 ${-days} 天`;
}

export function formatNoticeDay(date: string, isToday = false) {
  const label = dayjs(date).format('YYYY年M月D日');
  return isToday ? `${label}（今天）` : label;
}

export function fileUrl(path: string) {
  return path;
}

export function previewPageUrl(id: number, page: number) {
  return fileUrl(`/api/answers/${id}/preview/pages/${page}`);
}

export async function listStudents() {
  return request<Student[]>('/api/students', { method: 'GET' });
}

export async function createStudent(data: Partial<Student>) {
  return request<Student>('/api/students', { method: 'POST', data });
}

export async function updateStudent(id: number, data: Partial<Student>) {
  return request<Student>(`/api/students/${id}`, { method: 'PATCH', data });
}

export async function deleteStudent(id: number) {
  return request(`/api/students/${id}`, { method: 'DELETE' });
}

export async function listAdminStudents() {
  return request<Student[]>('/api/admin/students', { method: 'GET' });
}

export async function assignStudentTeachers(id: number, teacherIds: number[]) {
  return request<Student>(`/api/admin/students/${id}/teachers`, {
    method: 'PUT',
    data: { teacher_ids: teacherIds },
  });
}

export async function listAdminAnswers() {
  return request<AnswerKey[]>('/api/admin/answers', { method: 'GET' });
}

export async function assignAnswerTeachers(id: number, teacherIds: number[]) {
  return request<AnswerKey>(`/api/admin/answers/${id}/teachers`, {
    method: 'PUT',
    data: { teacher_ids: teacherIds },
  });
}

export async function assignStudentsTeachers(
  ids: number[],
  teacherIds: number[],
  mode: 'add' | 'replace' = 'add',
) {
  return request<{ updated: number }>('/api/admin/students/teachers', {
    method: 'PUT',
    data: { ids, teacher_ids: teacherIds, mode },
  });
}

export async function assignAnswersTeachers(
  ids: number[],
  teacherIds: number[],
  mode: 'add' | 'replace' = 'add',
) {
  return request<{ updated: number }>('/api/admin/answers/teachers', {
    method: 'PUT',
    data: { ids, teacher_ids: teacherIds, mode },
  });
}

export async function registerTeacher(data: {
  email: string;
  password: string;
  name: string;
  phone?: string;
  invite_code: string;
}) {
  return request<{ role: string; name: string }>('/api/auth/register', {
    method: 'POST',
    data,
    skipErrorHandler: true,
  });
}

export async function listTeacherDirectory() {
  return request<TeacherBrief[]>('/api/teachers/directory', { method: 'GET' });
}

export async function listTeachers() {
  return request<TeacherAccount[]>('/api/teachers', { method: 'GET' });
}

export async function createTeacherAccount(data: Partial<TeacherAccount> & { password: string }) {
  return request<TeacherAccount>('/api/teachers', { method: 'POST', data });
}

export async function updateTeacherAccount(id: number, data: Partial<TeacherAccount> & { password?: string }) {
  return request<TeacherAccount>(`/api/teachers/${id}`, { method: 'PATCH', data });
}

export async function createInviteCode(expiresInDays = 7) {
  return request<CreatedInviteCode>('/api/teachers/invites', {
    method: 'POST',
    data: { expires_in_days: expiresInDays },
  });
}

export async function listInviteCodes() {
  return request<InviteCode[]>('/api/teachers/invites', { method: 'GET' });
}

export async function revokeInviteCode(id: number) {
  return request(`/api/teachers/invites/${id}`, { method: 'DELETE' });
}

export async function getStudent(id: number) {
  return request<Student>(`/api/students/${id}`, { method: 'GET' });
}

export async function listGroups() {
  return request<Group[]>('/api/groups', { method: 'GET' });
}

export async function createGroup(data: { name: string; student_ids: number[] }) {
  return request<Group>('/api/groups', { method: 'POST', data });
}

export async function updateGroup(id: number, data: { name: string; student_ids: number[] }) {
  return request<Group>(`/api/groups/${id}`, { method: 'PATCH', data });
}

export async function deleteGroup(id: number) {
  return request(`/api/groups/${id}`, { method: 'DELETE' });
}

export async function listEvents(params?: { start?: string; end?: string; student_id?: number }) {
  return request<AcademicEvent[]>('/api/events', { method: 'GET', params });
}

export async function createEvent(data: Record<string, any>) {
  return request<AcademicEvent>('/api/events', { method: 'POST', data });
}

export async function createEventBatch(data: Record<string, any>) {
  return request<AcademicEvent[]>('/api/events/batch', { method: 'POST', data });
}

export async function updateEvent(id: number, data: Record<string, any>) {
  return request<AcademicEvent>(`/api/events/${id}`, { method: 'PATCH', data });
}

export async function deleteEvent(id: number) {
  return request(`/api/events/${id}`, { method: 'DELETE' });
}

export async function uploadAttachment(eventId: number, file: File) {
  const form = new FormData();
  form.append('file', file);
  return request<Attachment>(`/api/events/${eventId}/attachments`, {
    method: 'POST',
    data: form,
  });
}

export async function deleteAttachment(eventId: number, fileId: number) {
  return request(`/api/events/${eventId}/attachments/${fileId}`, { method: 'DELETE' });
}

export async function listHomework(params?: {
  homework_date?: string;
  group_id?: number;
  student_id?: number;
}) {
  return request<HomeworkItem[]>('/api/homework', { method: 'GET', params });
}

export async function batchHomework(data: {
  student_ids: number[];
  homework_date: string;
  subject: string;
  title: string;
}) {
  return request<HomeworkItem[]>('/api/homework/batch', { method: 'POST', data });
}

export async function updateHomework(id: number, data: Record<string, any>) {
  return request<HomeworkItem>(`/api/homework/${id}`, { method: 'PATCH', data });
}

export async function deleteHomework(id: number) {
  return request(`/api/homework/${id}`, { method: 'DELETE' });
}

export async function getToday(day?: string) {
  return request<TodayData>('/api/today', { method: 'GET', params: day ? { day } : {} });
}

export async function getPrintNotices(day?: string) {
  return request<PrintNotices>('/api/notifications', {
    method: 'GET',
    params: day ? { day } : {},
    skipErrorHandler: true,
  });
}

export async function markPrintNoticesRead(day?: string, taskIds: number[] = []) {
  return request<PrintNotices>('/api/notifications/read', {
    method: 'POST',
    data: { day, task_ids: taskIds },
  });
}

export async function patchReview(id: number, status: string) {
  return request<ReviewTask>(`/api/review-tasks/${id}`, { method: 'PATCH', data: { status } });
}

export async function updateProfile(data: Record<string, any>) {
  return request('/api/auth/me', { method: 'PATCH', data });
}

export async function listAnswers(params?: {
  q?: string;
  publisher?: string;
  subject?: string;
  grade?: string;
  school?: string;
}) {
  return request<AnswerKey[]>('/api/answers', { method: 'GET', params });
}

function answerForm(data: Record<string, any>, file?: File) {
  const form = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value !== undefined && value !== null) form.append(key, String(value));
  });
  if (file) form.append('file', file);
  return form;
}

export async function createAnswer(data: Record<string, any>, file?: File) {
  return request<AnswerKey>('/api/answers', { method: 'POST', data: answerForm(data, file) });
}

export async function updateAnswer(id: number, data: Record<string, any>, file?: File) {
  return request<AnswerKey>(`/api/answers/${id}`, { method: 'PATCH', data: answerForm(data, file) });
}

export async function deleteAnswer(id: number) {
  return request(`/api/answers/${id}`, { method: 'DELETE' });
}

export async function getAnswerPreview(id: number) {
  const response = await fetch(`/api/answers/${id}/preview`, {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error('preview failed');
  }
  return (await response.json()) as AnswerPreview;
}
